import { forwardRef } from 'react';
import type { ReactElement, ReactNode } from 'react';
import styled from '@emotion/styled';
import type { TippyProps } from '@tippyjs/react';
import Tippy from '@tippyjs/react';
import * as tokens from '@tokens';

export interface DropdownProps
  extends Omit<
    TippyProps,
    | 'appendTo'
    | 'arrow'
    | 'children'
    | 'content'
    | 'duration'
    | 'interactive'
    | 'offset'
    | 'placement'
    | 'trigger'
  > {
  /**
   * The element to append the dropdown to. Sometimes the dropdown needs to be appended
   * to a different DOM context due to accessibility, clipping, or z-index issues.
   * It should only be used when needed. By default the dropdown is appended to the document body.
   */
  appendTo?: HTMLElement | 'parent' | ((ref: Element) => Element);
  /**
   * Dropdown content.
   */
  children: ReactNode;
  /**
   * Styles override.
   */
  className?: string;
  /**
   * Disable the dropdown entirely, e.g. when the trigger button is disabled too.
   */
  isDisabled?: boolean;
  /**
   * Element to trigger the Dropdown. Must have an accessible ref.
   */
  trigger: ReactElement;
  /**
   * Event on which to trigger showing the dropdown. Defaults to 'click'. use 'none' if you want to not send a triggerEvent.
   */
  triggerEvent?: TippyProps['trigger'] | 'none';
  /**
   * Preferred dropdown placement. Defaults to 'bottom-start'
   */
  placement?: 'bottom-start' | 'bottom-end';
}

const StyledTippy = styled(Tippy, {
  shouldForwardProp: (prop: string) =>
    prop !== 'size' && prop !== 'actualWidth',
})`
  position: relative;
  outline: 0;
  overflow-y: auto;
  transition-property: transform, visibility, opacity;
  ${tokens.text('body2')};
  color: ${tokens.color.text_default};
  background-color: ${tokens.color.ui_background};
  ${tokens.elevation(2)};
  border: 1px solid ${tokens.color.ui_border};
  border-radius: ${tokens.radius.m};

  &[data-animation='fade'][data-state='hidden'] {
    opacity: 0;
  }

  &[data-inertia][data-state='visible'] {
    transition-timing-function: cubic-bezier(0.54, 1.5, 0.38, 1.11);
  }
`;

/**
 * Dropdown is a popover that can be attached to any trigger and contain anything inside.
 * It's used to build dropdown menus.
 *
 * {@link https://zeroheight.com/76c439891/p/96bb9e-dropdown-menu/b/72fb4d Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-dropdownmenu--base-dropdown Code documentation}
 *
 * @example
 * <Dropdown trigger={<IconButton />}>
 *   ...
 * </Dropdown>
 */
export const Dropdown = forwardRef<HTMLElement, DropdownProps>(
  (
    {
      appendTo = () => document.body,
      children,
      className,
      isDisabled = false,
      trigger,
      triggerEvent = 'click',
      placement = 'bottom-start',
      ...props
    }: DropdownProps,
    ref,
  ) => {
    return (
      <StyledTippy
        {...props}
        appendTo={appendTo}
        arrow={false}
        className={className}
        content={children}
        disabled={isDisabled}
        duration={100}
        interactive
        offset={[0, 8]}
        placement={placement}
        ref={ref}
        trigger={triggerEvent === 'none' ? undefined : triggerEvent}
        popperOptions={{ strategy: 'fixed' }}
      >
        {trigger}
      </StyledTippy>
    );
  },
);

Dropdown.displayName = 'Dropdown';
